import { useState, useEffect, useCallback } from 'react'
import { memorizationApi } from '../lib/api'
import type { MemorizationData } from '../types'

interface UseMemorizationOptions {
  autoLoad?: boolean
  status?: string
}

export function useMemorization(options: UseMemorizationOptions = {}) {
  const { autoLoad = true, status } = options

  const [verses, setVerses] = useState<MemorizationData[]>([])
  const [stats, setStats] = useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const loadVerses = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await memorizationApi.listVerses(status)
      setVerses(response)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load verses')
    } finally {
      setLoading(false)
    }
  }, [status])

  const loadStats = useCallback(async () => {
    try {
      const data = await memorizationApi.getStats()
      setStats(data)
    } catch (err) {
      // Silence AUTH_EXPIRED — these fire naturally when the user's session
      // lapses mid-page (the router will redirect to /auth on the same
      // tick). Logging them only adds noise to the console; the redirect
      // is the actual handling. Other failures still surface.
      const code = (err as { code?: string } | null | undefined)?.code
      if (code !== 'AUTH_EXPIRED' && import.meta.env.DEV) {
        console.error('Error loading stats:', err)
      }
    }
  }, [])

  const addVerse = useCallback(async (surahNumber: number, ayahNumber: number) => {
    try {
      setError(null)
      const verse = await memorizationApi.addVerse(surahNumber, ayahNumber)
      setVerses(prev => [verse, ...prev])
      loadStats()
      return verse
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to add verse'
      setError(message)
      throw err
    }
  }, [loadStats])

  // Optimistic remove: drop the row from local state immediately so the UI
  // updates without waiting for the API, then call DELETE in the
  // background. If the server rejects (network down, auth lapsed), restore
  // the row and surface the error so the caller can show a retry toast.
  const removeVerse = useCallback(
    async (surahNumber: number, ayahNumber: number) => {
      const snapshot = verses
      setError(null)
      setVerses((prev) =>
        prev.filter((v) => !(v.surahNumber === surahNumber && v.ayahNumber === ayahNumber)),
      )
      try {
        await memorizationApi.removeVerse(surahNumber, ayahNumber)
        loadStats()
      } catch (err) {
        // Roll back the optimistic removal.
        setVerses(snapshot)
        const message = err instanceof Error ? err.message : 'Failed to remove verse'
        setError(message)
        throw err
      }
    },
    [verses, loadStats],
  )

  const isAdded = useCallback((surahNumber: number, ayahNumber: number) => {
    return verses.some(
      v => v.surahNumber === surahNumber && v.ayahNumber === ayahNumber
    )
  }, [verses])

  /**
   * Toggle helper for the reader's per-verse memorize button. Returns the
   * NEW state (true = now in memorization). Caller can use it to drive
   * affordance / animation without reading verses again.
   */
  const toggleVerse = useCallback(
    async (surahNumber: number, ayahNumber: number): Promise<boolean> => {
      if (isAdded(surahNumber, ayahNumber)) {
        await removeVerse(surahNumber, ayahNumber)
        return false
      }
      await addVerse(surahNumber, ayahNumber)
      return true
    },
    [isAdded, addVerse, removeVerse],
  )

  useEffect(() => {
    if (autoLoad) {
      loadVerses()
      loadStats()
    }
  }, [autoLoad, loadVerses, loadStats])

  return {
    verses,
    stats,
    loading,
    error,
    loadVerses,
    loadStats,
    addVerse,
    removeVerse,
    toggleVerse,
    isAdded
  }
}

// (Removed) `useMemorizationTest` — a pre-`useGameSession` test runner that
// had no callers and a broken duration calculation (used test.completedAt,
// which the backend sets to creation time, as a start-time anchor). The
// active flow uses `useGameSession` which derives duration from its own
// `startRef` timestamp.
